import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { Instagram, Facebook, Linkedin, MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export const Footer = () => {
  const { t, lang } = useLang();
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then(r => setS(r.data)).catch(() => {}); }, []);

  const email = s.contact_email || "info@atlas-apartment.de";
  const phone = s.contact_phone || "+49 151 56634125";
  const address = s.contact_address || "Ursulastr. 7c, 46537 Dinslaken";
  const brandName = s.brand_name || "ATLAS";
  const brandSub = s.brand_sub || "Apartment";

  return (
    <footer className="bg-[var(--ink)] text-white">
      {/* CTA strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="tag mb-1">direkt buchen</p>
            <h3 className="font-display text-3xl md:text-4xl font-light">Bestpreis-Garantie</h3>
          </div>
          <Link to="/apartments"
            className="flex items-center gap-3 px-8 py-4 rounded-full border-[1.5px] border-[var(--terra)] text-[var(--terra)] hover:bg-[var(--terra)] hover:text-white text-sm font-semibold transition-all group">
            {t("book_apartment_footer")}
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          {s.logo_image
            ? <img src={s.logo_image} alt="Logo" className="h-9 object-contain mb-3 brightness-0 invert" />
            : <div>
                <span className="font-display text-3xl font-light">{brandName}</span>
                <p className="text-[10px] tracking-[0.3em] lowercase text-[var(--terra-light)]">{brandSub.toLowerCase()}</p>
              </div>
          }
          <p className="text-white/40 text-sm mt-4 leading-relaxed">
            {t("footer_tagline")}
          </p>
          <div className="flex gap-3 mt-6">
            {[{ icon: Instagram, label: "Instagram" }, { icon: Facebook, label: "Facebook" }, { icon: Linkedin, label: "LinkedIn" }].map(({ icon: Icon, label }) => (
              <a key={label} href="#" aria-label={label}
                className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-[var(--terra)] transition-all">
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs lowercase tracking-[0.05em] text-white/30 mb-5">{t("foot_discover")}</p>
          <ul className="space-y-3">
            {[
              { to: "/", label: t("nav_home") },
              { to: "/apartments", label: t("nav_apartments") },
              { to: "/apartments?city=Oberhausen", label: "Oberhausen" },
              { to: "/apartments?city=Dinslaken", label: "Dinslaken" },
            ].map(l => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-white/50 hover:text-white transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs lowercase tracking-[0.05em] text-white/30 mb-5">{t("foot_legal")}</p>
          <ul className="space-y-3">
            {[
              { to: "/impressum", label: t("foot_imprint") },
              { to: "/datenschutz", label: t("foot_privacy") },
              { to: "/agb", label: t("foot_terms") },
            ].map(l => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-white/50 hover:text-white transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs lowercase tracking-[0.05em] text-white/30 mb-5">{t("foot_contact")}</p>
          <ul className="space-y-4">
            <li>
              <a href={`mailto:${email}`} className="flex items-start gap-3 text-sm text-white/50 hover:text-white transition-colors">
                <Mail size={13} className="mt-0.5 shrink-0 text-[var(--terra-light)]" /> {email}
              </a>
            </li>
            <li>
              <a href={`tel:${phone.replace(/\s/g,'')}`} className="flex items-start gap-3 text-sm text-white/50 hover:text-white transition-colors">
                <Phone size={13} className="mt-0.5 shrink-0 text-[var(--terra-light)]" /> {phone}
              </a>
            </li>
            <li className="flex items-start gap-3 text-sm text-white/40">
              <MapPin size={13} className="mt-0.5 shrink-0 text-[var(--terra-light)]" /> {address}
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between text-xs text-white/20 gap-2">
          <span>© {new Date().getFullYear()} Atlas Apartment GbR · Viktor Schlosser & Maksym Diachenko</span>
          <span>{t("foot_rights")}</span>
        </div>
      </div>
    </footer>
  );
};
